import { useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { itemDeselected, itemSelected } from 'Vendor/Store/studio';
import { selectActivePageId } from 'Vendor/Store/studio/selectors';

interface MarqueeState {
    active: boolean;
    startX: number;
    startY: number;
    endX: number;
    endY: number;
}

export const useMarqueeSelection = (items: any[], isDisabled = false) => {
    const dispatch = useDispatch();
    const activePageId = useSelector(selectActivePageId);

    const [marquee, setMarquee] = useState<MarqueeState>({
        active: false,
        startX: 0,
        startY: 0,
        endX: 0,
        endY: 0,
    });
    const [justFinished, setJustFinished] = useState(false);

    const canvasRef = useRef<HTMLDivElement>(null);

    const getMousePosition = (e: React.PointerEvent) => {
        return { x: e.clientX, y: e.clientY }; // Page coordinates
    };

    const handlePointerDown = (e: React.PointerEvent) => {
        if (isDisabled || !activePageId) return;

        const target = e.target as HTMLElement;
        if (target.closest('.draggable-grid-item')) return; // don't start on item

        const { x, y } = getMousePosition(e);
        setMarquee({ active: true, startX: x, startY: y, endX: x, endY: y });
        setJustFinished(false);

        e.preventDefault();
    };

    const handlePointerMove = (e: React.PointerEvent) => {
        if (!marquee.active || isDisabled) return;
        const { x, y } = getMousePosition(e);
        setMarquee(prev => ({ ...prev, endX: x, endY: y }));
    };

    const handlePointerUp = () => {
        if (!marquee.active || isDisabled) return;

        const x1 = Math.min(marquee.startX, marquee.endX);
        const y1 = Math.min(marquee.startY, marquee.endY);
        const x2 = Math.max(marquee.startX, marquee.endX);
        const y2 = Math.max(marquee.startY, marquee.endY);

        dispatch(itemDeselected({ pageId: activePageId }));

        items.forEach(item => {
            const el = document.querySelector(`[data-id="${item.id}"]`) as HTMLElement;
            if (!el) return;

            const rect = el.getBoundingClientRect();
            const isIntersect = x1 < rect.right && x2 > rect.left && y1 < rect.bottom && y2 > rect.top;

            if (isIntersect && activePageId) {
                dispatch(itemSelected({ pageId: activePageId, itemId: item.id, metaKey: true }));
            }
        });

        setMarquee({ active: false, startX: 0, startY: 0, endX: 0, endY: 0 });
        setJustFinished(true);
    };

    return {
        canvasRef,
        marquee,
        isMarqueeActive: marquee.active || justFinished,
        handlePointerDown,
        handlePointerMove,
        handlePointerUp,
        setJustFinished,
    };
};
